import type { SiteTemplateDefinition, TemplateRenderProps } from "../types";
import { renderRestaurantTemplate, restaurantModernTemplate } from "./restaurantModern";

function PizzeriaStyles() {
    return (
        <style>{`
            /*
             * A hard, editorial pizza poster inspired by the Maximal visual
             * language: condensed type, an uncompromising three-colour system
             * and a precise 50/50 opening composition.
             */
            [data-web-template="restaurant-pizzeria"] {
                --pizzeria-black: var(--site-text, #090909);
                --pizzeria-red: var(--site-primary, #ca1f26);
                --pizzeria-white: var(--site-background, #fff);
                background: var(--pizzeria-white);
                color: var(--pizzeria-black);
                font-family: var(--font-manrope, Manrope), Inter, system-ui, sans-serif;
            }

            [data-web-template="restaurant-pizzeria"] :where(
                .site-builder-card,
                .site-builder-image,
                .site-builder-button,
                .site-builder-badge,
                .site-builder-input,
                .site-builder-textarea
            ) {
                border-radius: 0 !important;
                box-shadow: none !important;
            }

            [data-web-template="restaurant-pizzeria"] :where(
                .site-builder-text-h1,
                .site-builder-text-h2,
                .site-builder-text-h3
            ) {
                font-family: var(--font-barlow-condensed, "Barlow Condensed"), "Arial Narrow", Impact, sans-serif !important;
                font-stretch: condensed;
                font-weight: 900 !important;
                letter-spacing: -.045em !important;
                line-height: .88 !important;
                text-transform: uppercase;
            }

            [data-web-template="restaurant-pizzeria"] :where(
                .site-builder-button,
                .site-builder-badge,
                [data-document-role="headerNavigation"] .site-builder-text
            ) {
                font-family: var(--font-barlow-condensed, "Barlow Condensed"), "Arial Narrow", Impact, sans-serif !important;
                font-weight: 800 !important;
                letter-spacing: .08em !important;
                text-transform: uppercase;
            }

            [data-web-template="restaurant-pizzeria"] .site-builder-card:hover,
            [data-web-template="restaurant-pizzeria"] .site-builder-card:focus-within {
                box-shadow: none !important;
                transform: none !important;
            }

            /* Black utility navigation. */
            [data-web-template="restaurant-pizzeria"] [data-web-region="header"] {
                backdrop-filter: none !important;
                background: var(--pizzeria-black) !important;
                border: 0 !important;
                border-bottom: 6px solid var(--pizzeria-red) !important;
                box-shadow: none !important;
                min-height: 88px !important;
                position: relative !important;
                top: auto !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="header"][data-restaurant-header-scrolled] {
                backdrop-filter: none !important;
                background: var(--pizzeria-black) !important;
                border-bottom-color: var(--pizzeria-red) !important;
                box-shadow: none !important;
                min-height: 88px !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="header"] > .site-builder-grid {
                min-height: 82px;
                padding-inline: clamp(1.25rem, 3vw, 3.25rem);
                width: min(100%, 1480px);
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="header"] :where(
                .site-builder-text,
                .site-builder-text > span,
                .site-builder-icon
            ) {
                color: var(--pizzeria-white) !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="header"] :where(
                .site-navigation-primary-trigger,
                .site-navigation-primary-link,
                .site-navigation-primary-parent,
                .site-navigation-primary-toggle,
                .site-navigation-mobile-trigger,
                .site-navigation-mobile-link,
                .site-navigation-mobile-parent,
                .site-navigation-mobile-toggle
            ) {
                color: var(--pizzeria-white) !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="header"] [data-document-role="brand"] > .site-builder-badge {
                background: var(--pizzeria-red) !important;
                border: 0 !important;
                color: var(--pizzeria-white) !important;
                min-height: 48px;
                min-width: 48px;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="header"] [data-document-role="brand"] {
                align-items: center !important;
                display: flex !important;
                flex-wrap: nowrap !important;
                gap: 12px !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="header"] [data-document-role="brandCopy"] .site-builder-text:first-child {
                font-family: var(--font-barlow-condensed, "Barlow Condensed"), "Arial Narrow", Impact, sans-serif !important;
                font-size: clamp(1.35rem, 2.1vw, 2rem) !important;
                font-weight: 900 !important;
                letter-spacing: -.025em !important;
                line-height: .9 !important;
                text-transform: uppercase;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="header"] [data-document-role="brandCopy"] > [data-site-builder-block="text"]:last-child {
                display: none;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="header"] [data-document-role="headerNavigation"] {
                gap: clamp(1.25rem, 2.2vw, 2.75rem) !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="header"] [data-document-role="headerNavigation"] .site-builder-text {
                padding: .7rem 0 !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="header"] [data-document-role="headerNavigation"] .site-builder-text::after {
                background: var(--pizzeria-red) !important;
                border-radius: 0 !important;
                bottom: 0 !important;
                height: 5px !important;
                transform: none !important;
                width: 0 !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="header"] [data-document-role="headerNavigation"] .site-builder-text:hover::after {
                left: 0 !important;
                width: 100% !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="header"] [data-document-role="headerCta"] {
                background: var(--pizzeria-red) !important;
                border: 2px solid var(--pizzeria-red) !important;
                color: var(--pizzeria-white) !important;
                min-height: 48px !important;
                padding-inline: 1.4rem !important;
            }

            /* A true desktop split: white sales copy, uninterrupted image. */
            [data-web-template="restaurant-pizzeria"] [data-web-region="hero"] {
                align-items: stretch !important;
                background: var(--pizzeria-white) !important;
                min-height: calc(100svh - 88px) !important;
                overflow: hidden;
                padding: 0 !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="hero"]::before {
                background: var(--pizzeria-red) !important;
                clip-path: polygon(44% 0, 100% 0, 56% 100%, 0 100%);
                content: "";
                inset: 0 auto 0 calc(50% - 15px) !important;
                pointer-events: none;
                position: absolute;
                width: 30px;
                z-index: 3;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="hero"] > .site-builder-grid {
                align-items: stretch !important;
                gap: 0 !important;
                grid-template-columns: repeat(12, minmax(0, 1fr)) !important;
                min-height: calc(100svh - 88px) !important;
                width: 100% !important;
                max-width: none !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="hero"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="heroCopy"]) {
                align-self: stretch;
                display: grid;
                grid-column: 1 / 7 !important;
                grid-row: 1 !important;
                min-width: 0;
            }

            [data-web-template="restaurant-pizzeria"] [data-document-role="heroCopy"] {
                align-content: center !important;
                background: var(--pizzeria-white) !important;
                box-sizing: border-box;
                column-gap: 12px !important;
                padding: clamp(3rem, 6vw, 7rem) clamp(2rem, 5vw, 6rem) clamp(3rem, 5vw, 5.5rem) max(2rem, calc((100vw - 1480px) / 2 + 3.25rem));
                position: relative;
                row-gap: clamp(1rem, 2vh, 1.5rem) !important;
                width: 100%;
                z-index: 2;
            }

            [data-web-template="restaurant-pizzeria"] [data-document-role="heroCopy"]::before {
                background: var(--pizzeria-red);
                content: "";
                height: 10px;
                left: max(2rem, calc((100vw - 1480px) / 2 + 3.25rem));
                position: absolute;
                top: clamp(2.2rem, 5vw, 5rem);
                width: clamp(74px, 8vw, 132px);
            }

            [data-web-template="restaurant-pizzeria"] [data-document-role="heroHeading"] {
                color: var(--pizzeria-black) !important;
                font-family: var(--font-barlow-condensed, "Barlow Condensed"), "Arial Narrow", Impact, sans-serif !important;
                font-size: clamp(5.2rem, 8.2vw, 10rem) !important;
                font-weight: 900 !important;
                letter-spacing: -.075em !important;
                line-height: .72 !important;
                max-width: 6ch !important;
                text-transform: uppercase;
            }

            [data-web-template="restaurant-pizzeria"] [data-document-role="heroDescription"] {
                color: var(--pizzeria-black) !important;
                font-family: var(--font-manrope, Manrope), Inter, system-ui, sans-serif !important;
                font-size: clamp(1.15rem, 1.55vw, 1.45rem) !important;
                font-weight: 500 !important;
                line-height: 1.25 !important;
                max-width: 30rem;
                opacity: .72;
                width: 100% !important;
            }

            [data-web-template="restaurant-pizzeria"] :where(
                [data-document-role="heroPrimaryAction"],
                [data-document-role="heroSecondaryAction"]
            ) {
                background: transparent !important;
                border: 0 !important;
                justify-self: stretch !important;
                min-height: 0 !important;
                padding: 0 !important;
                width: 100% !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-document-role="heroPrimaryAction"] .site-builder-button {
                background: var(--pizzeria-red) !important;
                border-color: var(--pizzeria-red) !important;
                color: var(--pizzeria-white) !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-document-role="heroSecondaryAction"] .site-builder-button {
                background: var(--pizzeria-black) !important;
                color: var(--pizzeria-white) !important;
            }

            [data-web-template="restaurant-pizzeria"] :where(
                [data-document-role="heroPrimaryAction"],
                [data-document-role="heroSecondaryAction"]
            ) .site-builder-button {
                align-items: center;
                border: 3px solid var(--pizzeria-black) !important;
                border-radius: 0 !important;
                display: inline-flex;
                font-family: var(--font-barlow-condensed, "Barlow Condensed"), "Arial Narrow", Impact, sans-serif !important;
                font-size: .94rem !important;
                font-weight: 900 !important;
                justify-content: center;
                letter-spacing: .09em !important;
                min-height: 62px !important;
                padding: 1rem !important;
                text-transform: uppercase;
                width: 100% !important;
            }

            [data-web-template="restaurant-pizzeria"] :where(
                [data-document-role="heroPrimaryAction"],
                [data-document-role="heroSecondaryAction"]
            ) :where(.site-builder-button, .site-builder-button *, span, .site-builder-text, .site-builder-icon) {
                color: var(--pizzeria-white) !important;
            }

            [data-web-template="restaurant-pizzeria"] :where(
                [data-document-role="heroPrimaryAction"],
                [data-document-role="heroSecondaryAction"]
            ):hover .site-builder-button {
                background: var(--pizzeria-white) !important;
                border-color: var(--pizzeria-black) !important;
                color: var(--pizzeria-black) !important;
                transform: none !important;
            }

            [data-web-template="restaurant-pizzeria"] :where(
                [data-document-role="heroPrimaryAction"],
                [data-document-role="heroSecondaryAction"]
            ):hover :where(.site-builder-button, .site-builder-button *, span, .site-builder-text, .site-builder-icon) {
                color: var(--pizzeria-black) !important;
            }

            [data-web-template="restaurant-pizzeria"] :where(
                [data-document-role="heroHighlightOne"],
                [data-document-role="heroHighlightTwo"],
                [data-document-role="heroHighlightThree"]
            ) {
                align-items: center;
                background: var(--pizzeria-black) !important;
                border: 0 !important;
                border-top: 6px solid var(--pizzeria-red) !important;
                color: var(--pizzeria-white) !important;
                justify-content: flex-start;
                justify-self: stretch !important;
                min-height: 76px;
                padding: .8rem !important;
                width: 100% !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-document-role="heroHighlightTwo"] {
                background: var(--pizzeria-red) !important;
                border-top-color: var(--pizzeria-black) !important;
            }

            [data-web-template="restaurant-pizzeria"] :where(
                [data-document-role="heroHighlightOne"],
                [data-document-role="heroHighlightTwo"],
                [data-document-role="heroHighlightThree"]
            ) :where(.site-builder-text, .site-builder-text > span, .site-builder-icon) {
                color: var(--pizzeria-white) !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="hero"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="heroFeature"]) {
                align-self: stretch;
                display: grid;
                grid-column: 7 / 13 !important;
                grid-row: 1 !important;
                min-height: 0;
            }

            [data-web-template="restaurant-pizzeria"] [data-document-role="heroFeature"] {
                background: var(--pizzeria-black) !important;
                border: 0 !important;
                border-radius: 0 !important;
                box-shadow: none !important;
                gap: 0 !important;
                height: 100%;
                min-height: 0;
                overflow: hidden;
                padding: 0 !important;
                position: relative;
                transform: none !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-document-role="heroFeature"] > [data-site-builder-block="image"] {
                height: 100%;
                inset: 0;
                position: absolute;
                width: 100%;
            }

            [data-web-template="restaurant-pizzeria"] [data-document-role="heroFeature"] :where(.site-builder-image, img) {
                aspect-ratio: auto !important;
                border: 0 !important;
                height: 100% !important;
                object-fit: cover !important;
                width: 100% !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-document-role="heroFeature"]::after {
                background: var(--pizzeria-red);
                clip-path: polygon(24% 0, 100% 0, 100% 100%, 0 100%);
                content: "";
                height: clamp(38px, 5vw, 72px);
                position: absolute;
                right: 0;
                top: 0;
                width: clamp(120px, 15vw, 250px);
                z-index: 1;
            }

            [data-web-template="restaurant-pizzeria"] [data-document-role="heroOpeningHours"] {
                background: var(--pizzeria-black) !important;
                bottom: 0;
                gap: 0 !important;
                left: 0;
                position: absolute;
                right: 0;
                z-index: 2;
            }

            [data-web-template="restaurant-pizzeria"] [data-document-role="heroOpeningHours"] > .site-builder-column {
                background: var(--pizzeria-black) !important;
                border: 0 !important;
                border-left: 1px solid var(--pizzeria-white) !important;
                box-sizing: border-box;
                padding: 1.15rem 1.4rem !important;
                width: 50% !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-document-role="heroOpeningHours"] > .site-builder-column:first-child {
                background: var(--pizzeria-red) !important;
                border-left: 0 !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-document-role="heroOpeningHours"] :where(.site-builder-text, .site-builder-text > span) {
                color: var(--pizzeria-white) !important;
                font-family: var(--font-barlow-condensed, "Barlow Condensed"), "Arial Narrow", Impact, sans-serif !important;
                font-size: 1rem !important;
                font-weight: 800 !important;
                letter-spacing: .05em;
                text-transform: uppercase;
            }

            /* Shared editorial section grammar. */
            [data-web-template="restaurant-pizzeria"] :where(
                [data-web-region="menu"],
                [data-web-region="experience"],
                [data-web-region="gallery"],
                [data-web-region="occasions"],
                [data-web-region="reservation"]
            ) {
                border: 0 !important;
                padding-block: clamp(5rem, 9vw, 9rem) !important;
            }

            [data-web-template="restaurant-pizzeria"] :where(
                [data-web-region="menu"],
                [data-web-region="experience"],
                [data-web-region="gallery"],
                [data-web-region="occasions"],
                [data-web-region="reservation"]
            ) .site-builder-text-h2 {
                font-size: clamp(4rem, 7.5vw, 8.4rem) !important;
                max-width: 11ch;
            }

            /* Menu: product-sheet typography and hard price cards. */
            [data-web-template="restaurant-pizzeria"] [data-web-region="menu"] {
                background: var(--pizzeria-white) !important;
                color: var(--pizzeria-black) !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="menu"]::before {
                background: var(--pizzeria-black);
                content: "";
                height: 16px;
                left: 0;
                position: absolute;
                right: 38%;
                top: 0;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="menu"]::after {
                background: var(--pizzeria-red);
                content: "";
                height: 16px;
                position: absolute;
                right: 0;
                top: 0;
                width: 38%;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="menu"] :where(.site-builder-text, .site-builder-text > span) {
                color: var(--pizzeria-black) !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="menu"] > .site-builder-grid > .site-builder-grid-item:nth-child(2) > .site-builder-column {
                align-content: center;
                background: var(--pizzeria-black) !important;
                box-sizing: border-box;
                min-height: 100%;
                padding: clamp(1.5rem, 3vw, 2.75rem) !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="menu"] > .site-builder-grid > .site-builder-grid-item:nth-child(2) :where(.site-builder-badge, .site-builder-text, .site-builder-text > span, .site-builder-icon) {
                color: var(--pizzeria-white) !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="menu"] > .site-builder-grid > .site-builder-grid-item:nth-child(n + 3) .site-builder-card {
                background: var(--pizzeria-white) !important;
                border: 3px solid var(--pizzeria-black) !important;
                min-height: 100%;
                padding: clamp(1.25rem, 2.6vw, 2.25rem) !important;
                position: relative;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="menu"] > .site-builder-grid > .site-builder-grid-item:nth-child(3) .site-builder-card,
            [data-web-template="restaurant-pizzeria"] [data-web-region="menu"] > .site-builder-grid > .site-builder-grid-item:nth-child(6) .site-builder-card {
                background: var(--pizzeria-black) !important;
                color: var(--pizzeria-white) !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="menu"] > .site-builder-grid > .site-builder-grid-item:nth-child(3) .site-builder-card :where(.site-builder-text, .site-builder-text > span),
            [data-web-template="restaurant-pizzeria"] [data-web-region="menu"] > .site-builder-grid > .site-builder-grid-item:nth-child(6) .site-builder-card :where(.site-builder-text, .site-builder-text > span) {
                color: var(--pizzeria-white) !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="menu"] .site-builder-card-content-inline > .site-builder-badge {
                background: var(--pizzeria-red) !important;
                border: 0 !important;
                color: var(--pizzeria-white) !important;
                font-size: 1.1rem !important;
                padding: .85rem 1rem !important;
            }

            /* Three numbered statements read as one black/red utility band. */
            [data-web-template="restaurant-pizzeria"] [data-web-region="experience"] {
                background: var(--pizzeria-black) !important;
                color: var(--pizzeria-white) !important;
                padding-block: 0 !important;
                transform: none !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="experience"] > .site-builder-grid {
                gap: 0 !important;
                width: 100% !important;
                max-width: none !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="experience"] > .site-builder-grid > .site-builder-grid-item {
                border-right: 1px solid var(--pizzeria-white);
                min-height: clamp(300px, 33vw, 490px);
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="experience"] > .site-builder-grid > .site-builder-grid-item:last-child {
                border-right: 0;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="experience"] > .site-builder-grid > .site-builder-grid-item:nth-child(2) {
                background: var(--pizzeria-red);
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="experience"] > .site-builder-grid > .site-builder-grid-item > .site-builder-column {
                align-content: center;
                box-sizing: border-box;
                height: 100%;
                padding: clamp(2rem, 5vw, 5.5rem) !important;
                position: relative;
                transform: none !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="experience"] > .site-builder-grid > .site-builder-grid-item > .site-builder-column::after {
                border-bottom: 26px solid transparent;
                border-left: 26px solid var(--pizzeria-red);
                content: "";
                position: absolute;
                right: 0;
                top: 0;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="experience"] > .site-builder-grid > .site-builder-grid-item:nth-child(2) > .site-builder-column::after {
                border-left-color: var(--pizzeria-black);
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="experience"] :where(.site-builder-text, .site-builder-text > span, .site-builder-icon) {
                color: var(--pizzeria-white) !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="experience"] > .site-builder-grid > .site-builder-grid-item .site-builder-text:first-child {
                color: var(--pizzeria-red) !important;
                font-family: var(--font-barlow-condensed, "Barlow Condensed"), "Arial Narrow", Impact, sans-serif !important;
                font-size: clamp(5rem, 9vw, 9rem) !important;
                font-weight: 900 !important;
                letter-spacing: -.075em !important;
                line-height: .7 !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="experience"] > .site-builder-grid > .site-builder-grid-item:nth-child(2) .site-builder-text:first-child {
                color: var(--pizzeria-black) !important;
            }

            /* A black image wall with a deliberately asymmetric mosaic. */
            [data-web-template="restaurant-pizzeria"] [data-web-region="gallery"] {
                background: var(--pizzeria-black) !important;
                color: var(--pizzeria-white) !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="gallery"] :where(.site-builder-text, .site-builder-text > span, .site-builder-icon) {
                color: var(--pizzeria-white) !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="gallery"] > .site-builder-grid > .site-builder-grid-item:first-child {
                border-left: 12px solid var(--pizzeria-red);
                padding-left: clamp(1.25rem, 3vw, 3rem);
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="gallery"] > .site-builder-grid > .site-builder-grid-item:first-child > .site-builder-column {
                align-items: start !important;
                text-align: left !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="gallery"] > .site-builder-grid > .site-builder-grid-item:first-child .site-builder-text {
                text-align: left !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="gallery"] .site-builder-image {
                border: 0 !important;
                height: 100% !important;
                min-height: 260px;
                overflow: hidden;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="gallery"] .site-builder-image img {
                filter: grayscale(1) contrast(1.16);
                height: 100% !important;
                object-fit: cover !important;
                transition: filter 240ms ease !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="gallery"] .site-builder-image:hover img {
                filter: grayscale(0) contrast(1.06);
                transform: none !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="gallery"] > .site-builder-grid > .site-builder-grid-item:last-child > .site-builder-column {
                background: var(--pizzeria-red) !important;
                border: 0 !important;
                box-sizing: border-box;
                padding: clamp(1.75rem, 3.5vw, 3.5rem) !important;
            }

            /* White feature spread: image on the left, ruled use-cases right. */
            [data-web-template="restaurant-pizzeria"] [data-web-region="occasions"] {
                background: var(--pizzeria-white) !important;
                color: var(--pizzeria-black) !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="occasions"] :where(.site-builder-text, .site-builder-text > span) {
                color: var(--pizzeria-black) !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="occasions"] > .site-builder-grid > .site-builder-grid-item:first-child .site-builder-image {
                border-bottom: 16px solid var(--pizzeria-red) !important;
                height: 100% !important;
                min-height: 580px;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="occasions"] > .site-builder-grid > .site-builder-grid-item:first-child img {
                filter: grayscale(1) contrast(1.08);
                height: 100% !important;
                object-fit: cover !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="occasions"] > .site-builder-grid > .site-builder-grid-item:nth-child(2) > .site-builder-column {
                border-top: 10px solid var(--pizzeria-black);
                padding-top: clamp(1.5rem, 3vw, 3rem);
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="occasions"] .site-builder-card {
                background: var(--pizzeria-white) !important;
                border: 0 !important;
                border-top: 2px solid var(--pizzeria-black) !important;
                padding: 1.5rem 0 !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="occasions"] .site-builder-card:last-child {
                border-bottom: 2px solid var(--pizzeria-black) !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="occasions"] .site-builder-card .site-builder-text-h3 {
                font-size: clamp(1.75rem, 3vw, 2.75rem) !important;
            }

            /* Booking closes the page as a red/black campaign panel. */
            [data-web-template="restaurant-pizzeria"] [data-web-region="reservation"] {
                background: var(--pizzeria-red) !important;
                color: var(--pizzeria-white) !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="reservation"] :where(.site-builder-text, .site-builder-text > span, .site-builder-icon, label) {
                color: var(--pizzeria-white) !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="reservation"] > .site-builder-grid > .site-builder-grid-item:first-child > .site-builder-column {
                border-top: 12px solid var(--pizzeria-white);
                padding-top: clamp(1.5rem, 3vw, 3rem);
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="reservation"] > .site-builder-grid > .site-builder-grid-item:nth-child(2) > .site-builder-column {
                background: var(--pizzeria-black) !important;
                box-sizing: border-box;
                padding: clamp(2rem, 4vw, 4rem) !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="reservation"] :where(input, textarea, select) {
                background: var(--pizzeria-white) !important;
                border: 3px solid var(--pizzeria-black) !important;
                border-radius: 0 !important;
                color: var(--pizzeria-black) !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="reservation"] button,
            [data-web-template="restaurant-pizzeria"] [data-web-region="reservation"] .site-builder-button {
                background: var(--pizzeria-black) !important;
                border: 3px solid var(--pizzeria-black) !important;
                border-radius: 0 !important;
                color: var(--pizzeria-white) !important;
                font-weight: 900 !important;
                text-transform: uppercase;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="footer"] {
                background: var(--pizzeria-black) !important;
                border: 0 !important;
                border-top: 16px solid var(--pizzeria-red) !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="footer"] :where(.site-builder-text, .site-builder-text > span, .site-builder-icon) {
                color: var(--pizzeria-white) !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="footer"] a:hover :where(.site-builder-text, .site-builder-text > span) {
                color: var(--pizzeria-red) !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="fullMenu"] .site-builder-badge {
                background: var(--pizzeria-red) !important;
                border-color: var(--pizzeria-red) !important;
                color: var(--pizzeria-white) !important;
            }

            [data-web-template="restaurant-pizzeria"] [data-web-region="fullMenu"] .site-builder-badge :where(
                .site-builder-text,
                .site-builder-text > span,
                .site-builder-icon
            ) {
                color: var(--pizzeria-white) !important;
            }

            @media (max-width: 900px) {
                [data-web-template="restaurant-pizzeria"] [data-web-region="header"] {
                    min-height: 72px !important;
                }

                [data-web-template="restaurant-pizzeria"] [data-web-region="header"] > .site-builder-grid {
                    min-height: 66px;
                    padding-inline: 1.15rem;
                }

                [data-web-template="restaurant-pizzeria"] [data-web-region="header"] .restaurant-mobile-menu-toggle {
                    background: var(--pizzeria-red) !important;
                    border: 0 !important;
                    border-radius: 0 !important;
                    opacity: 1;
                    pointer-events: auto;
                    top: 11px;
                    transform: none;
                }

                [data-web-template="restaurant-pizzeria"] [data-web-region="header"] .restaurant-mobile-menu-toggle > span {
                    background: var(--pizzeria-white) !important;
                    border-radius: 0 !important;
                }

                [data-web-template="restaurant-pizzeria"] [data-web-region="header"][data-restaurant-mobile-menu-open] {
                    background: var(--pizzeria-black) !important;
                    border-bottom: 0 !important;
                }

                [data-web-template="restaurant-pizzeria"] [data-web-region="hero"] {
                    min-height: auto !important;
                }

                [data-web-template="restaurant-pizzeria"] [data-web-region="hero"]::before {
                    clip-path: none;
                    height: 12px;
                    inset: calc(56svh + 72px) 0 auto !important;
                    width: 100%;
                }

                [data-web-template="restaurant-pizzeria"] [data-web-region="hero"] > .site-builder-grid {
                    display: grid !important;
                    grid-template-columns: minmax(0, 1fr) !important;
                    grid-template-rows: auto auto !important;
                    min-height: auto !important;
                }

                [data-web-template="restaurant-pizzeria"] [data-web-region="hero"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="heroCopy"]) {
                    grid-column: 1 !important;
                    grid-row: 1 !important;
                }

                [data-web-template="restaurant-pizzeria"] [data-document-role="heroCopy"] {
                    grid-template-columns: repeat(6, minmax(0, 1fr)) !important;
                    min-height: 56svh;
                    padding: clamp(3.5rem, 11vw, 6rem) 1.25rem clamp(3rem, 9vw, 5rem) !important;
                }

                [data-web-template="restaurant-pizzeria"] [data-document-role="heroCopy"]::before {
                    height: 8px;
                    left: 1.25rem;
                    top: 2rem;
                    width: 84px;
                }

                [data-web-template="restaurant-pizzeria"] [data-document-role="heroHeading"],
                [data-web-template="restaurant-pizzeria"] [data-document-role="heroDescription"],
                [data-web-template="restaurant-pizzeria"] [data-document-role="heroPrimaryAction"],
                [data-web-template="restaurant-pizzeria"] [data-document-role="heroSecondaryAction"] {
                    grid-column: 1 / -1 !important;
                }

                [data-web-template="restaurant-pizzeria"] [data-document-role="heroHeading"] {
                    font-size: clamp(4.25rem, 20vw, 7.5rem) !important;
                    max-width: 5.6ch !important;
                }

                [data-web-template="restaurant-pizzeria"] [data-document-role="heroPrimaryAction"],
                [data-web-template="restaurant-pizzeria"] [data-document-role="heroSecondaryAction"] {
                    min-height: 58px !important;
                    width: 100% !important;
                }

                [data-web-template="restaurant-pizzeria"] [data-document-role="heroHighlightOne"] {
                    grid-column: 1 / span 2 !important;
                }

                [data-web-template="restaurant-pizzeria"] [data-document-role="heroHighlightTwo"] {
                    grid-column: 3 / span 2 !important;
                }

                [data-web-template="restaurant-pizzeria"] [data-document-role="heroHighlightThree"] {
                    grid-column: 5 / span 2 !important;
                }

                [data-web-template="restaurant-pizzeria"] :where(
                    [data-document-role="heroHighlightOne"],
                    [data-document-role="heroHighlightTwo"],
                    [data-document-role="heroHighlightThree"]
                ) {
                    min-height: 72px;
                    padding: .65rem !important;
                }

                [data-web-template="restaurant-pizzeria"] :where(
                    [data-document-role="heroHighlightOne"],
                    [data-document-role="heroHighlightTwo"],
                    [data-document-role="heroHighlightThree"]
                ) .site-builder-icon {
                    display: none;
                }

                [data-web-template="restaurant-pizzeria"] [data-web-region="hero"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="heroFeature"]) {
                    grid-column: 1 !important;
                    grid-row: 2 !important;
                    height: clamp(420px, 78vw, 680px);
                }

                [data-web-template="restaurant-pizzeria"] [data-document-role="heroFeature"] {
                    display: block !important;
                    height: 100%;
                }

                [data-web-template="restaurant-pizzeria"] [data-web-region="experience"] > .site-builder-grid > .site-builder-grid-item > .site-builder-column {
                    padding: clamp(2rem, 5vw, 3.5rem) !important;
                }
            }

            @media (max-width: 767px) {
                [data-web-template="restaurant-pizzeria"] :where(
                    [data-web-region="menu"],
                    [data-web-region="gallery"],
                    [data-web-region="occasions"],
                    [data-web-region="reservation"]
                ) {
                    padding-inline: 1rem !important;
                }

                [data-web-template="restaurant-pizzeria"] :where(
                    [data-web-region="menu"],
                    [data-web-region="experience"],
                    [data-web-region="gallery"],
                    [data-web-region="occasions"],
                    [data-web-region="reservation"]
                ) .site-builder-text-h2 {
                    font-size: clamp(3.5rem, 15vw, 6rem) !important;
                }

                [data-web-template="restaurant-pizzeria"] [data-web-region="experience"] > .site-builder-grid {
                    grid-template-columns: minmax(0, 1fr) !important;
                }

                [data-web-template="restaurant-pizzeria"] [data-web-region="experience"] > .site-builder-grid > .site-builder-grid-item {
                    border-bottom: 1px solid var(--pizzeria-white);
                    border-right: 0;
                    grid-column: 1 !important;
                    grid-row: auto !important;
                    min-height: 260px;
                }

                [data-web-template="restaurant-pizzeria"] [data-web-region="experience"] > .site-builder-grid > .site-builder-grid-item:last-child {
                    border-bottom: 0;
                }

                [data-web-template="restaurant-pizzeria"] [data-web-region="gallery"] .site-builder-image,
                [data-web-template="restaurant-pizzeria"] [data-web-region="occasions"] > .site-builder-grid > .site-builder-grid-item:first-child .site-builder-image {
                    min-height: clamp(320px, 105vw, 560px);
                }

                [data-web-template="restaurant-pizzeria"] [data-web-region="reservation"] > .site-builder-grid > .site-builder-grid-item:nth-child(2) > .site-builder-column {
                    padding: 1.5rem !important;
                }
            }

            @media (max-width: 480px) {
                [data-web-template="restaurant-pizzeria"] [data-document-role="heroCopy"] {
                    padding-inline: .85rem !important;
                }

                [data-web-template="restaurant-pizzeria"] [data-document-role="heroCopy"]::before {
                    left: .85rem;
                }

                [data-web-template="restaurant-pizzeria"] :where(
                    [data-document-role="heroHighlightOne"],
                    [data-document-role="heroHighlightTwo"],
                    [data-document-role="heroHighlightThree"]
                ) {
                    font-size: .67rem !important;
                    letter-spacing: .02em !important;
                    padding-inline: .35rem !important;
                }

                [data-web-template="restaurant-pizzeria"] [data-document-role="heroOpeningHours"] > .site-builder-column {
                    padding: .85rem !important;
                }
            }
        `}</style>
    );
}

export const restaurantPizzeriaTemplate: SiteTemplateDefinition = {
    manifest: {
        ...restaurantModernTemplate.manifest,
        code: "restaurant-pizzeria",
        name: "Pizzerie",
        description: "Výrazná černo-bílo-červená šablona pro městské pizzerie, pec, rozvoz a živou atmosféru.",
        previewImage: "/templates/restaurant-pizzeria/preview.webp",
        defaults: {
            ...restaurantModernTemplate.manifest.defaults,
            colors: {
                primary: "#ca1f26",
                accent: "#ca1f26",
                background: "#ffffff",
                surface: "#ffffff",
                text: "#090909",
                muted: "#090909",
                border: "#090909",
            },
            typography: {
                heading: "Barlow Condensed, Arial Narrow, Impact, sans-serif",
                body: "Manrope, Inter, system-ui, sans-serif",
            },
            layout: {
                container: "1480px",
                sectionSpacing: "spacious",
                radius: "0px",
            },
            components: {
                header: "builder",
                footer: "builder",
                buttons: "solid",
                forms: "flat",
            },
        },
    },
    render: (props: TemplateRenderProps) => renderRestaurantTemplate(
        restaurantPizzeriaTemplate,
        props,
        <PizzeriaStyles />,
    ),
};
